import * as Sentry from "@sentry/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { AxiosError } from "axios";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAuthStore } from "../../auth/authStore";
import type { BankIDAuthConfig, BankIDError } from "../common-types";
import {
  authenticateViaBankID,
  cancelViaBankID,
  collectViaBankID,
} from "./api";
import {
  type ISEBankIDCollectResponse,
  SEBankIDAuthType,
  SEBankIDFlowState,
  SEBankIDHintCode,
  type SEBankIDState,
} from "./types";

const BANKID_COLLECT_QUERY_KEY = "se-bankid-collect";
const POLL_INTERVAL = 2000;

interface UseSEBankIDConfig extends BankIDAuthConfig {
  /**
   * SSN for testing purposes (SE only, non-production)
   */
  testSSN?: string;
  extraData?: {
    preferredLanguage?: string | null;
    gaClientId?: string | null;
  };
}

export const useSEBankID = (config: UseSEBankIDConfig) => {
  const {
    authenticationContext,
    identificationContext,
    onSuccess,
    onError,
    onCancel,
    testSSN,
    extraData,
  } = config;

  const { t } = useTranslation();
  const { setAccessToken, setRefreshToken } = useAuthStore();
  const queryClient = useQueryClient();
  const [authType, setAuthType] = useState<SEBankIDAuthType | null>(null);
  const [state, setState] = useState<SEBankIDState>({ status: "idle" });
  const [qrCode, setQrCode] = useState<string | null>(null);
  // Track if the component is mounted to prevent double starts
  const isMountedRef = useRef(true);

  // Track if authentication is active to prevent double starts
  const isAuthenticatingRef = useRef(false);

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
      queryClient.cancelQueries({
        queryKey: [BANKID_COLLECT_QUERY_KEY],
        exact: false,
      });
    };
  }, [queryClient]);

  // Authenticate mutation
  const authenticateMutation = useMutation({
    mutationFn: async () => {
      const response = await authenticateViaBankID(testSSN);

      return response.data;
    },
    onSuccess: (data) => {
      if (!isMountedRef.current) return;

      if (data.qrCode) setQrCode(data.qrCode);

      setState({
        status: "pending",
        authType: authType ?? undefined,
        hintCode: SEBankIDHintCode.STARTED,
      });
      isAuthenticatingRef.current = false;
    },
    onError: (err) => {
      if (!isMountedRef.current) return;

      console.error("Failed to start BankID authentication", err);
      Sentry.captureException(err);

      const error: BankIDError = {
        code: "AUTH_START_FAILED",
        message: "Failed to start authentication",
      };
      setState({
        status: "error",
        error,
        hintCode: SEBankIDHintCode.START_FAILED,
      });
      isAuthenticatingRef.current = false;

      if (onError) onError(error);
    },
  });

  const resetAuthentication = useCallback(() => {
    authenticateMutation.reset();
    setQrCode(null);
  }, [authenticateMutation]);

  const resetBankIdApplication = useCallback(() => {
    queryClient.cancelQueries({ queryKey: [BANKID_COLLECT_QUERY_KEY] });
    queryClient.resetQueries({
      queryKey: [BANKID_COLLECT_QUERY_KEY],
      exact: false,
    });
    resetAuthentication();
    isAuthenticatingRef.current = false;
  }, [queryClient, resetAuthentication]);

  // Cancel mutation
  const cancelMutation = useMutation({
    mutationFn: async () => {
      if (authenticateMutation.data?.orderRef) {
        await cancelViaBankID(authenticateMutation.data.orderRef);
      }
    },
    onMutate: () => {
      resetBankIdApplication();
    },
  });

  // Collect query - polls for authentication status
  const collectQuery = useQuery<ISEBankIDCollectResponse, AxiosError>({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: [BANKID_COLLECT_QUERY_KEY, authenticateMutation.data?.orderRef],
    queryFn: async () => {
      const orderRef = authenticateMutation.data?.orderRef;
      if (!orderRef) throw new Error("No orderRef available");

      const response = await collectViaBankID({
        orderRef,
        extraData: extraData ?? null,
        identificationContext: identificationContext ?? null,
        authenticationContext,
      });

      return response.data;
    },
    enabled:
      !!authenticateMutation.data?.orderRef && state.status === "pending",
    refetchInterval: (query) => {
      // Stop polling if we have a terminal state
      if (
        query.state.data?.status === SEBankIDFlowState.COMPLETE ||
        query.state.data?.status === SEBankIDFlowState.FAILED
      ) {
        return false;
      }

      if (import.meta.env.MODE === "test") {
        return 200;
      }

      return POLL_INTERVAL;
    },
    refetchIntervalInBackground: true,
    retry: false,
    staleTime: 0,
  });

  // Handle collect query results
  useEffect(() => {
    if (!collectQuery.data || !isMountedRef.current) return;

    const { status, hintCode, qrCode: newQrCode, tokens } = collectQuery.data;

    // Update QR code if it changed
    if (newQrCode && newQrCode !== qrCode) {
      setQrCode(newQrCode);
    }

    if (
      status === SEBankIDFlowState.PENDING &&
      hintCode &&
      !cancelMutation.isPending
    ) {
      setState((prev) => {
        if ("hintCode" in prev && prev.hintCode === hintCode) return prev;

        return {
          status: "pending",
          hintCode,
        };
      });
    }

    if (status === SEBankIDFlowState.COMPLETE) {
      // Authentication successful
      setState({ status: "success" });
      resetAuthentication();

      setAccessToken(tokens.accessToken);
      setRefreshToken(tokens.refreshToken);

      onSuccess({
        accessToken: tokens.accessToken,
        refreshToken: tokens.refreshToken,
      });
    } else if (status === SEBankIDFlowState.FAILED) {
      // Authentication failed
      const isUserCancellation = hintCode === SEBankIDHintCode.USER_CANCEL;

      if (isUserCancellation) {
        setState({ status: "cancelled", hintCode });
      } else if (hintCode === SEBankIDHintCode.START_FAILED) {
        // Restart authentication on START_FAILED
        console.warn("BankID start failed, restarting...");
        queryClient.setQueryData(
          [BANKID_COLLECT_QUERY_KEY, authenticateMutation.data?.orderRef],
          {
            status: SEBankIDFlowState.PENDING,
            hintCode: SEBankIDHintCode.OUTSTANDING_TRANSACTION,
          },
        );

        // Trigger a new authenticate call
        if (!isAuthenticatingRef.current && authType) {
          isAuthenticatingRef.current = true;
          authenticateMutation.mutate();
        }

        return;
      } else {
        const error: BankIDError = {
          code: hintCode ?? "UNKNOWN_ERROR",
          message: "Authentication failed",
          isUserCancellation,
        };
        console.error(
          "Failed to collect BankID authentication",
          collectQuery.data,
        );

        Sentry.captureMessage(
          `SE BankID error code: ${JSON.stringify(collectQuery.data)}`,
        );
        setState({
          status: "error",
          error,
          hintCode: hintCode ?? SEBankIDHintCode.ERROR,
        });

        if (onError) onError(error);
      }

      resetAuthentication();
    }
  }, [
    collectQuery.data,
    qrCode,
    setQrCode,
    resetAuthentication,
    onSuccess,
    onError,
    queryClient,
    authenticateMutation.data?.orderRef,
    authType,
    authenticateMutation,
    cancelMutation.isPending,
    setAccessToken,
    setRefreshToken,
  ]);

  // Handle collect query errors
  useEffect(() => {
    if (!collectQuery.error || !isMountedRef.current) return;

    let error: BankIDError | undefined;

    if (collectQuery.error.status === 403) {
      error = {
        code: collectQuery.error.code ?? "UNAUTHORIZED",
        message: t("bankID.errors.unauthorized"),
      };
    } else {
      console.error("Collecting BankID failed:", collectQuery.error);
      Sentry.captureException(collectQuery.error);

      error = {
        code: "COLLECT_FAILED",
        message: t("bankID.errors.genericError"),
      };
    }

    setState({ status: "error", error, hintCode: SEBankIDHintCode.ERROR });
    resetAuthentication();

    if (onError) onError(error);
  }, [collectQuery.error, resetAuthentication, onError, t]);

  // Start authentication
  const start = useCallback(
    (newAuthType?: SEBankIDAuthType) => {
      if (isAuthenticatingRef.current) {
        console.warn("Authentication already in progress");

        return;
      }

      const typeToUse = newAuthType ?? SEBankIDAuthType.QR_CODE;
      setAuthType(typeToUse);
      setState({ status: "authenticating", authType: typeToUse });
      isAuthenticatingRef.current = true;
      authenticateMutation.mutate();
    },
    [authenticateMutation],
  );

  // Reset to initial state
  const reset = useCallback(() => {
    resetBankIdApplication();
    setAuthType(null);
    setState({ status: "idle" });
  }, [resetBankIdApplication]);

  // Cancel authentication
  const cancel = useCallback(() => {
    cancelMutation.mutate(undefined, {
      onSuccess: () => {
        if (onCancel) onCancel();
        setAuthType(null);
        setState({ status: "idle" });
      },
    });
  }, [cancelMutation, onCancel]);

  // Change auth type (switch between QR and auto-start)
  const changeAuthType = useCallback(
    (newAuthType: SEBankIDAuthType) => {
      if (state.status !== "pending") {
        console.warn("Can only change auth type during pending state");

        return;
      }

      // Cancel current authentication
      if (authenticateMutation.data?.orderRef) {
        cancelMutation.mutate();
      }

      // Start with new auth type
      setAuthType(newAuthType);
      setState({ status: "authenticating", authType: newAuthType });
      isAuthenticatingRef.current = true;
      authenticateMutation.mutate();
    },
    [state.status, authenticateMutation, cancelMutation],
  );

  return {
    state,
    start,
    cancel,
    reset,
    changeAuthType,
    qrCode,
    autoStartToken: authenticateMutation.data?.autoStartToken,
    authType,
  };
};
